// ── user_request helpers ──────────────────────────────────────────────────────

export function parseUserRequest(userRequest) {
  if (!userRequest) return {};
  if (typeof userRequest === 'object') return userRequest;
  if (userRequest.startsWith('{')) {
    try { return JSON.parse(userRequest); } catch (e) {}
  }
  return { topic: userRequest };
}

export function getTaskTitle(instance) {
  if (!instance) return 'Untitled Project';
  const parsed = parseUserRequest(instance.user_request);
  return parsed.topic
    || parsed.user_request
    || instance.title
    || `${parsed.document_type || 'Content Project'} #${instance.id}`;
}

export function getTaskFormat(instance) {
  if (!instance) return '';
  const parsed = parseUserRequest(instance.user_request);
  return parsed.document_type
    || instance.content_format
    || instance.workflow?.name
    || 'Content';
}

export function getTaskStyle(instance) {
  if (!instance) return '';
  const parsed = parseUserRequest(instance.user_request);
  return parsed.writing_style || '';
}

export function getWordCount(inst) {
  const cm = inst?.context_memory || {};
  const content = cm.final_content
    || cm.step_7_final_approval__strategy_check
    || cm.step_6_multi_layered_editing
    || cm.step_4_first_draft_creation
    || '';
  if (!content) return null;
  const count = content.split(/\s+/).filter(Boolean).length;
  return count > 1000 ? (count / 1000).toFixed(1) + 'K words' : count + ' words';
}

export function formatTimeAgo(dateStr) {
  if (!dateStr) return 'recently';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  const days = Math.floor(hrs / 24);
  return `${days} day${days > 1 ? 's' : ''} ago`;
}

// ── stats ─────────────────────────────────────────────────────────────────────

export function calculateStats(instances) {
  const completed = instances.filter(i => i.status === 'completed');
  const inProgress = instances.filter(i => i.status === 'in_progress');

  // Word counts from final_content
  const totalWords = completed.reduce((sum, inst) => {
    const content = inst.context_memory?.final_content || '';
    return sum + (content ? content.split(/\s+/).filter(Boolean).length : 0);
  }, 0);

  // Avg completion time in hours
  let avgHours = null;
  if (completed.length > 0) {
    const times = completed
      .filter(i => i.created_at && i.updated_at)
      .map(i => {
        const start = new Date(i.created_at);
        const end = new Date(i.updated_at);
        return (end - start) / (1000 * 60 * 60);
      })
      .filter(h => h > 0);
    if (times.length > 0) {
      avgHours = (times.reduce((a, b) => a + b, 0) / times.length).toFixed(1);
    }
  }

  // Writing style distribution — check context_memory and user_request
  const styleCounts = {};
  completed.forEach(i => {
    const parsed = parseUserRequest(i.user_request);
    const style = i.context_memory?.writing_style || parsed.writing_style || 'Unknown';
    styleCounts[style] = (styleCounts[style] || 0) + 1;
  });

  // Format distribution — check context_memory and user_request
  const formatCounts = {};
  completed.forEach(i => {
    const parsed = parseUserRequest(i.user_request);
    const format = i.context_memory?.content_format || parsed.document_type || i.workflow?.name || 'Unknown';
    formatCounts[format] = (formatCounts[format] || 0) + 1;
  });

  // This month's instances
  const now = new Date();
  const thisMonthInstances = instances.filter(i => {
    const d = new Date(i.created_at);
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  });

  // This month words
  const thisMonthWords = thisMonthInstances
    .filter(i => i.status === 'completed')
    .reduce((sum, inst) => {
      const content = inst.context_memory?.final_content || '';
      return sum + (content ? content.split(/\s+/).filter(Boolean).length : 0);
    }, 0);

  // Format word count for display
  function formatWords(n) {
    if (n === 0) return '0';
    if (n >= 1000) return (n / 1000).toFixed(0) + 'K';
    return n.toString();
  }

  return {
    total: instances.length,
    completed: completed.length,
    inProgress: inProgress.length,
    totalWords: formatWords(totalWords),
    avgHours: avgHours ? avgHours + ' hrs' : '—',
    styleCounts,
    formatCounts,
    thisMonthCount: thisMonthInstances.length,
    thisMonthWords: formatWords(thisMonthWords),
    // Estimate agent hours: avg ~0.5h per completed workflow step
    agentHours: completed.length > 0
      ? Math.round(
          completed.reduce((sum, i) => sum + Object.keys(i.completed_steps || {}).length, 0) * 0.5
        ).toString()
      : '0',
  };
}
